'use client';

import React, { useEffect, useState } from 'react';
import { MessageCircle, Dumbbell, Apple } from 'lucide-react';

export default function DashboardLoader({ onComplete }: { onComplete?: () => void }) {
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [completed, setCompleted] = useState(false);

  const features = [
    {
      title: "Your AI Training & Nutrition Coach",
      subtitle: "Powered by AI",
      icon: MessageCircle,
    },
    {
      title: "Create Custom Training Plans",
      subtitle: "Tailored to your goals",
      icon: Dumbbell,
    },
    {
      title: "Create Custom Nutrition Plans",
      subtitle: "Optimized for performance",
      icon: Apple,
    }
  ];

  useEffect(() => {
    // Smooth progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setCompleted(true);
          // Wait 500ms after reaching 100% before calling onComplete
          setTimeout(() => onComplete?.(), 500);
          return 100;
        }
        return prev + 1;
      });
    }, 30);

    // Cycle through features every 2 seconds (faster)
    const featureInterval = setInterval(() => {
      setActiveSection(prev => (prev + 1) % features.length);
    }, 2000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(featureInterval);
    };
  }, [onComplete]);

  const currentFeature = features[activeSection];
  const Icon = currentFeature.icon;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-between p-6 md:p-12 overflow-hidden">
      
      {/* Your Logo at Top */}
      <div className="w-full flex justify-center pt-4">
        <div className="relative">
          {/* Input Logo - White Version */}
          <img 
            src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/Inputperformanceaiwhite.png" 
            alt="Input" 
            className="h-12 md:h-16 w-auto"
          />
          
          {/* Optional: Keep orbital ring animation with your logo */}
          <div className="absolute inset-0 -m-2 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-green-400/20 animate-spin-slow" />
          </div>
        </div>
      </div>

      {/* Main Content Area - Floating Icon */}
      <div className="flex-1 flex items-center justify-center w-full max-w-2xl px-4">
        <div className="w-full flex flex-col items-center space-y-8 md:space-y-12">
          
          {/* Large Floating Icon - No background, no border */}
          <div className="relative">
            {/* Main rotating icon */}
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div
                  key={index}
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${
                    index === activeSection 
                      ? 'opacity-100 scale-100 rotate-0' 
                      : 'opacity-0 scale-50 rotate-45'
                  }`}
                >
                  <FeatureIcon 
                    className="w-32 h-32 md:w-48 md:h-48 text-green-400 drop-shadow-[0_0_30px_rgba(167,139,250,0.6)]" 
                    strokeWidth={1.5}
                  />
                </div>
              );
            })}
            
            {/* Spacer to maintain layout */}
            <div className="w-32 h-32 md:w-48 md:h-48" />
            
            {/* Subtle glow effect behind icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 md:w-56 md:h-56 bg-green-400/10 rounded-full blur-3xl animate-pulse" />
            </div>
          </div>

          {/* Text Content */}
          <div className="text-center space-y-3 md:space-y-4 animate-fade-in">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-px w-8 md:w-12 bg-gradient-to-r from-transparent to-green-400" />
              <span className="text-green-400 font-semibold text-sm md:text-base">
                Step {activeSection + 1} of {features.length}
              </span>
              <div className="h-px w-8 md:w-12 bg-gradient-to-l from-transparent to-green-400" />
            </div>
            
            <h2 className="text-3xl md:text-5xl font-bold text-[#0f172a] leading-tight px-4">
              {currentFeature.title}
            </h2>
            
            <p className="text-lg md:text-2xl text-gray-500 font-light">
              {currentFeature.subtitle}
            </p>
          </div>

          {/* Feature Pills - Quick Navigation */}
          <div className="flex justify-center gap-3 md:gap-4">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div
                  key={index}
                  className={`flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full transition-all duration-500 ${
                    index === activeSection
                      ? 'bg-green-400 scale-110'
                      : index < activeSection
                        ? 'bg-green-400/30'
                        : 'bg-black/5'
                  }`}
                >
                  <FeatureIcon 
                    className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${
                      index === activeSection
                        ? 'text-[#0f172a]'
                        : index < activeSection
                          ? 'text-green-400'
                          : 'text-gray-600'
                    }`}
                    strokeWidth={2}
                  />
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* Bottom Progress Section */}
      <div className="w-full max-w-md space-y-4">
        {/* Progress Bar */}
        <div className="w-full">
          <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-300 ease-out relative overflow-hidden"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/30 to-transparent animate-shimmer" />
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-gray-600">Setting up your dashboard...</p>
            <p className="text-sm text-gray-500 font-medium">{progress}%</p>
          </div>
        </div>

        {/* Animated Step Indicators */}
        <div className="flex justify-center gap-2">
          {features.map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full transition-all duration-500 ${
                index === activeSection 
                  ? 'w-12 bg-green-400 shadow-[0_0_10px_rgba(167,139,250,0.5)]' 
                  : index < activeSection 
                    ? 'w-6 bg-green-400/50' 
                    : 'w-6 bg-black/10'
              }`}
            />
          ))}
        </div>

        {/* Brand */}
        <p className="text-center text-sm text-gray-600 mt-4">
          <span className="font-semibold text-[#0f172a]">Input</span>
        </p>
      </div>
    </div>
  );
}
